/// <amd-module name="Build/ApprovalsPanel/ApprovalReassignDialog" />
import * as React from "react";

import * as Resources from "Build/ApprovalsPanel/Resources"
import { ApprovalStep, ApprovalDetails } from "Build/Client/Contracts/DataProvider";
import * as TelemetryService from "Build/ApprovalsPanel/ChecksTelemetryService"

import { ObservableValue } from "VSS/Core/Observable";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";

import { Dialog } from "VSSUI/Dialog";
import { Icon } from "VSSUI/Icon";
import { IdentityPickerDropdown, IIdentity } from "VSSUI/IdentityPicker";
import { useComponentContext } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { TextField, TextFieldWidth } from "VSSUI/TextField";

export interface IApprovalReassignDialogProps {
    approvalDetails: ApprovalDetails;
    approvalStep: ApprovalStep;
    onCloseDialog: () => void;
    showDialog: boolean;
    onReassign: (approval: ApprovalDetails, approvalstep: ApprovalStep, reassignTo: IdentityRef, message?: string) => Promise<void>;
}

export const ApprovalReassignDialog: React.SFC<IApprovalReassignDialogProps> = props => {

    const commentObservable = new ObservableValue<string>("");
    const reassignIdentityObservable = new ObservableValue<IIdentity | undefined>(undefined);
    const reassignDisabledObservable = new ObservableValue<boolean>(true);
    const showSpinnerObservable = new ObservableValue<boolean>(false);
    const errorObservable = new ObservableValue<string>("");

    const context = useComponentContext();

    const onIdentityChange = (identity?: IIdentity) => {
        reassignIdentityObservable.value = identity;
        reassignDisabledObservable.value = !identity;
    };

    const filterIdentities = (returnedEntities: IIdentity[]): IIdentity[] => {
        return returnedEntities && returnedEntities.filter((entitity: IIdentity) => { return entitity.localId !== null });
    }

    const onReassignClick = () => {
        if (reassignIdentityObservable.value) {
            let reassignIdentity: IIdentity = reassignIdentityObservable.value;
            
            let reassignedApprover = {
                displayName: reassignIdentity.displayName,
                id: reassignIdentity.localId,
                descriptor: reassignIdentity.subjectDescriptor,
                imageUrl: reassignIdentity.image,
                uniqueName: reassignIdentity.mail
            } as IdentityRef;

            showSpinnerObservable.value = true;

            props.onReassign(props.approvalDetails, props.approvalStep, reassignedApprover, commentObservable.value).then(() => {
                // publish details to telemetry
                TelemetryService.publishEvent(context.pageContext, TelemetryService.Features.ApprovalReassign, {
                    "ApprovalId": props.approvalDetails.id,
                    "AssignedApprover": props.approvalStep.assignedApprover.id,
                    "ReassignTo": reassignedApprover.id
                });
                },(error) => {
                    errorObservable.value = (error && error.message) || error;
                    showSpinnerObservable.value = false;
            });
        }
    }

    const pickerProvider = new PeoplePickerProvider(context.pageContext, 
        {
            identityTypes: ["user", "group"],
            options: {
                MinResults: 1,
                MaxResults: 20
                    }
        },
        filterIdentities
        );

    return (
        props.showDialog ? (
            <Observer isReassignDisabled={reassignDisabledObservable} showSpinner={showSpinnerObservable} error={errorObservable}>
                {(reassignProps: { isReassignDisabled: boolean, showSpinner: boolean, error: string }) =>
                    <Dialog
                        titleProps={{ text: Resources.Reassign }}
                        footerButtonProps={[
                            {
                                text: Resources.Cancel,
                                onClick: props.onCloseDialog,
                                disabled: reassignProps.showSpinner
                            },
                            {
                                text: Resources.Reassign,
                                onClick: onReassignClick,
                                disabled: reassignProps.isReassignDisabled || reassignProps.showSpinner,
                                primary: true
                            }
                        ]}
                        onDismiss={props.onCloseDialog}
                    >
                        {!!reassignProps.error &&
                            <div className="reassign-error-message-section" tabIndex={0}>
                                <Icon ariaLabel="Error icon" iconName="ErrorBadge" className="reassign-error-icon" />
                                {reassignProps.error}
                            </div>}
                        {Resources.ReassignMessage}
                        <div className="reassign-approver-header">
                            {Resources.Approver}
                        </div>
                        <IdentityPickerDropdown
                            onChange={onIdentityChange}
                            pickerProvider={pickerProvider}
                            value={reassignIdentityObservable}
                            disabled={reassignProps.showSpinner}
                        />
                        <div className="reassign-comment-header">
                            {Resources.Comment}
                        </div>
                        <TextField
                            ariaLabel={Resources.Comment}
                            disabled={reassignProps.showSpinner}
                            value={commentObservable}
                            onChange={(e, newValue) => (commentObservable.value = newValue)}
                            multiline
                            rows={4}
                            width={TextFieldWidth.auto}
                        />
                        {reassignProps.showSpinner ? <Spinner className="pipeline-reassign-spinner" label={Resources.ReassignSpinnerLabel} /> : <div />}
                    </Dialog>
                }
            </Observer>
        ) : <div />
    );
}
